/**
 * Mission 引擎 —— 对应技术设计 §7「实验步骤脚本」。
 *
 * 步骤状态机：当前步骤 → 判定 → 通过则推进 / 判错则记账并给知识点 / blocked 则提示前置。
 *
 * 【§22 红线 3】「Mission 不写业务代码，新增实验主要写 JSON 配置」——
 * 这个文件里没有任何具体实验的痕迹，步骤全部来自 `mission.json`。
 * B2 门禁（tests/redlines.test.ts）会守着这一点。
 */

import type { LabMode } from "../data/schemas.js";
import type { EvalContext } from "../logic/condition-evaluator.js";
import { buildHint, MODE_HINT_LEVEL, type Hint, type HintLevel } from "./hint-engine.js";
import {
  checkStep,
  compileSteps,
  stepProgress,
  type CompiledStep,
  type StepDefinition,
  type StepVerdict,
} from "./step-checker.js";

export interface MissionDefinition {
  schemaVersion: string;
  id: string;
  title: string;
  grade?: string;
  modeDefaults?: Partial<
    Record<LabMode, { showGhost?: boolean; showPorts?: boolean | string; hintLevel?: HintLevel }>
  >;
  steps: StepDefinition[];
}

export interface StepRecord {
  stepId: string;
  status: "pending" | "passed" | "skipped";
  /** 相对毫秒（v1.4 §1.2） */
  startedAt: number;
  passedAt?: number;
  errorCount: number;
  blockedCount: number;
  /** 判错时触发过的知识点 */
  knowledgeShown: string[];
}

export interface MissionState {
  missionId: string;
  currentIndex: number;
  records: StepRecord[];
  /** 全部步骤走完 */
  completed: boolean;
  completedAt?: number;
}

export interface StepTransition {
  verdict: StepVerdict;
  /** 本次判定是否让流程前进了一步 */
  advanced: boolean;
  /** 判错时要触发的知识点 id */
  knowledge: string[];
  /** 通过或推进后的新状态 */
  state: MissionState;
}

export class MissionEngine {
  private readonly steps: CompiledStep[];
  private state: MissionState;

  constructor(
    private readonly definition: MissionDefinition,
    startedAt = 0,
  ) {
    this.steps = compileSteps(definition.steps);
    this.state = {
      missionId: definition.id,
      currentIndex: 0,
      completed: definition.steps.length === 0,
      records: definition.steps.map((step, index) => ({
        stepId: step.id,
        status: "pending",
        startedAt: index === 0 ? startedAt : -1,
        errorCount: 0,
        blockedCount: 0,
        knowledgeShown: [],
      })),
    };
  }

  getState(): MissionState {
    return this.state;
  }

  getSteps(): readonly StepDefinition[] {
    return this.definition.steps;
  }

  currentStep(): StepDefinition | null {
    return this.definition.steps[this.state.currentIndex] ?? null;
  }

  private currentRecord(): StepRecord | undefined {
    return this.state.records[this.state.currentIndex];
  }

  hintLevelFor(mode: LabMode): HintLevel {
    return this.definition.modeDefaults?.[mode]?.hintLevel ?? MODE_HINT_LEVEL[mode];
  }

  /** 当前步骤期望建立的关系，喂给吸附评分的「当前任务」维度（§5.3）。 */
  desiredRelationKeys(): ReadonlySet<string> {
    return new Set(this.currentStep()?.desiredRelations ?? []);
  }

  /**
   * 当前步骤要动到的器材 id —— 从 `desiredRelations` 里解析出来。
   * 这是 §7.1 `modeDefaults.showGhost` 与 §14.1「目标影子」的数据源：
   * 学生站在某一步不知道该拖什么，就是因为这个信息从来没送到渲染层。
   *
   * 关系键形如 `pour:kmno4_1.outlet->test_tube_1.inside`。
   */
  currentStepObjectIds(): string[] {
    const ids = new Set<string>();
    for (const key of this.currentStep()?.desiredRelations ?? []) {
      const body = key.includes(":") ? key.slice(key.indexOf(":") + 1) : key;
      for (const side of body.split("->")) {
        const objectId = side.trim().split(".")[0];
        if (objectId) ids.add(objectId);
      }
    }
    return [...ids];
  }

  /** 本模式是否显示目标影子（§7.1 `modeDefaults.showGhost`）。 */
  showGhostFor(mode: LabMode): boolean {
    return this.definition.modeDefaults?.[mode]?.showGhost ?? mode === "learn";
  }

  hint(mode: LabMode, now: number): Hint | null {
    const step = this.currentStep();
    const record = this.currentRecord();
    if (!step || !record) return null;
    return buildHint({
      step,
      mode,
      elapsedMs: record.startedAt >= 0 ? now - record.startedAt : 0,
      errorCount: record.errorCount,
      overrideLevel: this.hintLevelFor(mode),
    });
  }

  progress(context: EvalContext): { met: number; total: number } {
    const step = this.steps[this.state.currentIndex];
    return step ? stepProgress(step, context) : { met: 0, total: 0 };
  }

  /**
   * 评估当前步骤。每次图/状态变化后调用一次。
   *
   * 判错**不回退、不卡死**（§14.1）：只记一次 errorCount 并给知识点，
   * 学生改正后同一次 evaluate 就能通过。这是 §5.7「错误不是失败，而是可教学事件」。
   */
  evaluate(context: EvalContext, now: number): StepTransition {
    const step = this.steps[this.state.currentIndex];
    const record = this.currentRecord();
    if (!step || !record || this.state.completed) {
      return { verdict: "pending", advanced: false, knowledge: [], state: this.state };
    }

    const verdict = checkStep(step, context);

    if (verdict === "passed") {
      const records = [...this.state.records];
      records[this.state.currentIndex] = { ...record, status: "passed", passedAt: now };

      const nextIndex = this.state.currentIndex + 1;
      const completed = nextIndex >= this.definition.steps.length;
      if (!completed && records[nextIndex]) {
        records[nextIndex] = { ...records[nextIndex]!, startedAt: now };
      }

      this.state = {
        ...this.state,
        records,
        currentIndex: completed ? this.state.currentIndex : nextIndex,
        completed,
        ...(completed ? { completedAt: now } : {}),
      };
      return { verdict, advanced: true, knowledge: [], state: this.state };
    }

    if (verdict === "failed") {
      const records = [...this.state.records];
      const knowledge = step.definition.knowledgeOnError ?? [];
      records[this.state.currentIndex] = {
        ...record,
        errorCount: record.errorCount + 1,
        knowledgeShown: [...new Set([...record.knowledgeShown, ...knowledge])],
      };
      this.state = { ...this.state, records };
      return { verdict, advanced: false, knowledge, state: this.state };
    }

    if (verdict === "blocked") {
      const records = [...this.state.records];
      records[this.state.currentIndex] = { ...record, blockedCount: record.blockedCount + 1 };
      this.state = { ...this.state, records };
      return { verdict, advanced: false, knowledge: [], state: this.state };
    }

    return { verdict, advanced: false, knowledge: [], state: this.state };
  }

  /** 教师裁剪或调试用：跳过当前步骤。 */
  skipCurrent(now: number): MissionState {
    const record = this.currentRecord();
    if (!record || this.state.completed) return this.state;
    const records = [...this.state.records];
    records[this.state.currentIndex] = { ...record, status: "skipped", passedAt: now };
    const nextIndex = this.state.currentIndex + 1;
    const completed = nextIndex >= this.definition.steps.length;
    if (!completed && records[nextIndex]) {
      records[nextIndex] = { ...records[nextIndex]!, startedAt: now };
    }
    this.state = {
      ...this.state,
      records,
      currentIndex: completed ? this.state.currentIndex : nextIndex,
      completed,
      ...(completed ? { completedAt: now } : {}),
    };
    return this.state;
  }
}
